import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { calendarService, userPreferencesService, userService } from '../services/api';
import { schedulingService } from '../services/schedulingService';
import {
  CalendarDays, Clock, Settings, Calendar as CalendarIcon, Plus, Edit, Trash2,
  ChevronDown, ChevronUp, Palette, Briefcase, Tag, User, Users, Circle, X, Check
} from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SyncEventButton from '../components/calendar/SyncEventButton';
import CalendarEventForm from '../components/calendar/CalendarEventForm';
import CustomAgendaView from '../components/calendar/CustomAgendaView';
import EventTooltip from '../components/calendar/EventTooltip';
import useModal from '../hooks/useModal';
import useNotification from '../hooks/useNotification';
import useGoogleCalendarSync from '../hooks/useGoogleCalendarSync';
import useOutlookCalendarSync from '../hooks/useOutlookCalendarSync';
import Notification from '../components/common/Notification';
import ConfirmDialog from '../components/common/ConfirmDialog';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../styles/calendar.css';

const localizer = momentLocalizer(moment);

// Color palette for cases (auto-assigned based on case ID hash)
const CASE_COLORS = [
  { bg: '#3b82f6', border: '#2563eb', name: 'Blue' },
  { bg: '#8b5cf6', border: '#7c3aed', name: 'Purple' },
  { bg: '#06b6d4', border: '#0891b2', name: 'Cyan' },
  { bg: '#10b981', border: '#059669', name: 'Emerald' },
  { bg: '#f59e0b', border: '#d97706', name: 'Amber' },
  { bg: '#ef4444', border: '#dc2626', name: 'Red' },
  { bg: '#ec4899', border: '#db2777', name: 'Pink' },
  { bg: '#6366f1', border: '#4f46e5', name: 'Indigo' },
  { bg: '#14b8a6', border: '#0d9488', name: 'Teal' },
  { bg: '#f97316', border: '#ea580c', name: 'Orange' },
  { bg: '#84cc16', border: '#65a30d', name: 'Lime' },
  { bg: '#a855f7', border: '#9333ea', name: 'Violet' },
];

// Event type colors (for type-based coloring)
const EVENT_TYPE_COLORS = {
  court_date: { bg: '#ef4444', border: '#dc2626', label: 'Court Date' },
  meeting: { bg: '#8b5cf6', border: '#7c3aed', label: 'Meeting' },
  deadline: { bg: '#f59e0b', border: '#d97706', label: 'Deadline' },
  call: { bg: '#10b981', border: '#059669', label: 'Call' },
  consultation: { bg: '#06b6d4', border: '#0891b2', label: 'Consultation' },
  research: { bg: '#6366f1', border: '#4f46e5', label: 'Research' },
  task: { bg: '#84cc16', border: '#65a30d', label: 'Task' },
  appointment: { bg: '#3b82f6', border: '#2563eb', label: 'Appointment' },
  other: { bg: '#64748b', border: '#475569', label: 'Other' },
};

// Color scheme options
const COLOR_SCHEMES = {
  case: { id: 'case', label: 'By Case', icon: Briefcase, description: 'Color events by their associated case' },
  type: { id: 'type', label: 'By Type', icon: Tag, description: 'Color events by type (meeting, court date, etc.)' },
  attorney: { id: 'attorney', label: 'By Attorney', icon: User, description: 'Color events by assigned attorney' },
  minimal: { id: 'minimal', label: 'Minimal', icon: Circle, description: 'Single color for all events' },
};

// Generate a consistent color for a case based on its ID
const getCaseColor = (caseId) => {
  if (!caseId) return CASE_COLORS[0];

  // Simple hash function to get consistent color
  let hash = 0;
  for (let i = 0; i < caseId.length; i++) {
    hash = ((hash << 5) - hash) + caseId.charCodeAt(i);
    hash = hash & hash;
  }
  return CASE_COLORS[Math.abs(hash) % CASE_COLORS.length];
};

// Generate a consistent color for an attorney based on their ID
const getAttorneyColor = (attorneyId) => {
  if (!attorneyId) return CASE_COLORS[0];

  let hash = 0;
  for (let i = 0; i < attorneyId.length; i++) {
    hash = ((hash << 5) - hash) + attorneyId.charCodeAt(i);
    hash = hash & hash;
  }
  return CASE_COLORS[Math.abs(hash) % CASE_COLORS.length];
};

const Calendar = () => {
  const { user } = useAuth();
  const { notification, showSuccess, showError, hideNotification } = useNotification();
  const [searchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [todaysEvents, setTodaysEvents] = useState([]);
  const [blockedPeriods, setBlockedPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [googleCalendarStatus, setGoogleCalendarStatus] = useState({ connected: false });
  const [outlookCalendarStatus, setOutlookCalendarStatus] = useState({ connected: false });
  const [showEventForm, setShowEventForm] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showEventDetails, setShowEventDetails] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState('week');
  const [isTodayScheduleExpanded, setIsTodayScheduleExpanded] = useState(true);
  const [urlDateSet, setUrlDateSet] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [colorScheme, setColorScheme] = useState('case');
  const [showColorSchemeMenu, setShowColorSchemeMenu] = useState(false);
  const [firmUsers, setFirmUsers] = useState([]);
  const [selectedStaffIds, setSelectedStaffIds] = useState(new Set([user?.id]));
  const [showStaffFilter, setShowStaffFilter] = useState(false);
  const [showTasks, setShowTasks] = useState(true);

  // ESC key to dismiss menus
  useEffect(() => {
    if (!showColorSchemeMenu && !showStaffFilter) return;
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        setShowColorSchemeMenu(false);
        setShowStaffFilter(false);
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [showColorSchemeMenu, showStaffFilter]);

  // Fetch firm users for staff filter
  useEffect(() => {
    const fetchFirmUsers = async () => {
      try {
        const response = await userService.getUsers();
        const activeUsers = (response.data || []).filter(u => u.firm_active !== false && !u.has_pending_invitation);
        setFirmUsers(activeUsers);
      } catch (error) {
        console.error('Error fetching firm users:', error);
      }
    };
    fetchFirmUsers();
  }, []);

  // Load color scheme preference from API
  useEffect(() => {
    const loadColorScheme = async () => {
      try {
        const response = await userPreferencesService.getCalendarSettings();
        if (response.data?.calendar_settings?.color_scheme) {
          setColorScheme(response.data.calendar_settings.color_scheme);
        }
      } catch (error) {
        console.error('Error loading calendar preferences:', error);
      }
    };
    loadColorScheme();
  }, []);

  // Save color scheme preference to API
  const handleColorSchemeChange = async (newScheme) => {
    const previousScheme = colorScheme;
    setColorScheme(newScheme);
    setShowColorSchemeMenu(false);

    try {
      const response = await userPreferencesService.getCalendarSettings();
      const currentSettings = response.data || {
        calendar_settings: {},
        privacy_settings: { use_generic_titles: true }
      };

      await userPreferencesService.updateCalendarSettings({
        calendar_settings: {
          ...currentSettings.calendar_settings,
          color_scheme: newScheme
        },
        privacy_settings: currentSettings.privacy_settings
      });
    } catch (error) {
      console.error('Error saving color scheme preference:', error);
      setColorScheme(previousScheme); // Revert on error
    }
  };

  // Build legend items based on current color scheme and visible events
  const legendItems = useMemo(() => {
    const allEvents = [...events];

    switch (colorScheme) {
      case 'case': {
        const cases = new Map();
        allEvents.forEach(event => {
          if (event.resource?.caseId && event.resource?.caseTitle) {
            cases.set(event.resource.caseId, {
              id: event.resource.caseId,
              title: event.resource.caseTitle,
              color: getCaseColor(event.resource.caseId)
            });
          }
        });
        return Array.from(cases.values()).slice(0, 8); // Limit to 8 for UI
      }
      case 'type': {
        const types = new Set();
        allEvents.forEach(event => {
          if (event.resource?.type) {
            types.add(event.resource.type);
          }
        });
        return Array.from(types).map(type => ({
          id: type,
          title: EVENT_TYPE_COLORS[type]?.label || type.replace('_', ' '),
          color: EVENT_TYPE_COLORS[type] || EVENT_TYPE_COLORS.other
        })).slice(0, 8);
      }
      case 'attorney': {
        const attorneys = new Map();
        allEvents.forEach(event => {
          const id = event.resource?.assignedToId;
          const name = event.resource?.assignedToName;
          if (id && name) {
            attorneys.set(id, {
              id,
              title: name,
              color: getAttorneyColor(id)
            });
          }
        });
        return Array.from(attorneys.values()).slice(0, 8);
      }
      case 'minimal':
      default:
        return [];
    }
  }, [events, colorScheme]);

  // Handle URL parameters for date navigation
  useEffect(() => {
    const dateParam = searchParams.get('date');
    if (dateParam) {
      try {
        const targetDate = new Date(dateParam);
        if (!isNaN(targetDate.getTime())) {
          setCurrentDate(targetDate);
          setUrlDateSet(true);
        }
      } catch (error) {
        console.error('Invalid date parameter:', dateParam);
      }
    }
  }, [searchParams]);

  // Handle scroll position for calendar
  useEffect(() => {
    if (currentView === 'week' || currentView === 'day') {
      const timer = setTimeout(() => {
        const timeContent = document.querySelector('.rbc-time-content');
        if (timeContent) {
          const allEvents = [...events, ...blockedPeriods];
          const visibleEvents = allEvents.filter(event => {
            const eventDate = moment(event.start);
            if (currentView === 'day') {
              return eventDate.isSame(currentDate, 'day');
            } else {
              const weekStart = moment(currentDate).startOf('week');
              const weekEnd = moment(currentDate).endOf('week');
              return eventDate.isBetween(weekStart, weekEnd, 'day', '[]');
            }
          });

          let scrollToHour = 7;
          if (visibleEvents.length > 0) {
            const earliestHour = Math.min(...visibleEvents.map(event =>
              new Date(event.start).getHours()
            ));
            if (earliestHour < 7) {
              scrollToHour = earliestHour;
            }
          }

          const pixelsPerHour = timeContent.scrollHeight / 24;
          timeContent.scrollTop = scrollToHour * pixelsPerHour;
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [events, blockedPeriods, currentView, currentDate]);

  useEffect(() => {
    fetchTodaysEvents();
    fetchEventsForCurrentView();
    fetchBlockedPeriods();
    checkGoogleCalendarStatus();
    checkOutlookCalendarStatus();
  }, [currentDate, currentView, selectedStaffIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const getDateRangeForView = (date, view) => {
    const startOfView = moment(date);
    const endOfView = moment(date);

    switch (view) {
      case 'month':
        startOfView.startOf('month').subtract(1, 'week');
        endOfView.endOf('month').add(1, 'week');
        break;
      case 'week':
        startOfView.startOf('week');
        endOfView.endOf('week');
        break;
      case 'day':
        startOfView.startOf('day');
        endOfView.endOf('day');
        break;
      case 'agenda':
        startOfView.startOf('month');
        endOfView.endOf('month').add(2, 'months');
        break;
      default:
        startOfView.startOf('week');
        endOfView.endOf('week');
    }

    return {
      start: startOfView.format('YYYY-MM-DD'),
      end: endOfView.format('YYYY-MM-DD')
    };
  };

  const fetchBlockedPeriods = useCallback(async () => {
    if (!user?.id) return;

    try {
      const { start, end } = getDateRangeForView(currentDate, currentView);
      const startDateTime = `${start}T00:00:00`;
      const endDateTime = `${end}T23:59:59`;

      const response = await schedulingService.getLockedTimePeriods(null, startDateTime, endDateTime);

      if (!response.data || !Array.isArray(response.data)) {
        setBlockedPeriods([]);
        return;
      }

      const blockedEvents = response.data.map(period => ({
        id: `blocked-${period.id}`,
        title: period.reason || 'Blocked Time',
        start: new Date(period.start_time),
        end: new Date(period.end_time),
        allDay: false,
        resource: {
          type: 'blocked_time',
          isBlocked: true,
          reason: period.reason,
          description: period.description,
          isRecurring: period.is_recurring,
          rawPeriod: period
        }
      }));

      setBlockedPeriods(blockedEvents);
    } catch (error) {
      console.error('Error fetching blocked periods:', error);
      setBlockedPeriods([]);
    }
  }, [user?.id, currentDate, currentView]);

  const fetchEventsForCurrentView = useCallback(async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      const { start, end } = getDateRangeForView(currentDate, currentView);
      // If viewing only own calendar, pass user_id for efficiency
      // Otherwise fetch all firm events and filter client-side
      const viewingSelf = selectedStaffIds.size === 1 && selectedStaffIds.has(user.id);
      const userId = viewingSelf ? user.id : null;
      const response = await calendarService.getEventsInDateRange(userId, start, end);

      let filteredEvents = response.data.events;
      // If viewing specific staff (not just self), filter client-side
      if (!viewingSelf && selectedStaffIds.size > 0) {
        filteredEvents = filteredEvents.filter(event =>
          selectedStaffIds.has(event.assigned_to_id)
        );
      }

      const calendarEvents = filteredEvents.map(event => ({
        id: event.id,
        title: event.title,
        start: new Date(event.start_time),
        end: new Date(event.end_time),
        allDay: event.all_day || false,
        resource: {
          type: event.event_type,
          caseId: event.case_id,
          caseTitle: event.case_title,
          clientId: event.client_id,
          clientName: event.client_name,
          projectId: event.project_id,
          location: event.location,
          description: event.description,
          syncEnabled: event.sync_enabled,
          assignedToId: event.assigned_to_id,
          assignedToName: event.assigned_to_name,
          rawEvent: event
        }
      }));

      // Fetch tasks with due dates for calendar display
      let taskEvents = [];
      if (showTasks) {
        try {
          const { start, end } = getDateRangeForView(currentDate, currentView);
          const viewingSelf = selectedStaffIds.size === 1 && selectedStaffIds.has(user.id);
          const taskUserId = viewingSelf ? user.id : null;
          const tasksResponse = await calendarService.getTasksForCalendar(taskUserId, start, end);
          let filteredTasks = tasksResponse.data.tasks || [];
          if (!viewingSelf && selectedStaffIds.size > 0) {
            filteredTasks = filteredTasks.filter(t => selectedStaffIds.has(t.assigned_to_id));
          }
          taskEvents = filteredTasks.map(task => ({
            id: `task-${task.id}`,
            title: `\u2611 ${task.title}`,
            start: new Date(task.due_date),
            end: new Date(task.due_date),
            allDay: true,
            resource: {
              type: 'task_due',
              isTask: true,
              taskId: task.id,
              caseId: task.case_id,
              caseTitle: task.case_title,
              projectName: task.project_name,
              priority: task.priority,
              status: task.status,
              assignedToId: task.assigned_to_id,
              assignedToName: task.assigned_to_name
            }
          }));
        } catch (error) {
          console.error('Error fetching tasks for calendar:', error);
        }
      }

      setEvents([...calendarEvents, ...taskEvents]);
    } catch (error) {
      console.error('Error fetching events:', error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id, currentDate, currentView, selectedStaffIds, showTasks]);

  const fetchTodaysEvents = useCallback(async () => {
    if (!user?.id) return;

    try {
      const response = await calendarService.getTodaysEvents(user.id);
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
      const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

      const filteredEvents = (response.data.events || []).filter(event => {
        const eventStart = new Date(event.start_time);
        return eventStart >= todayStart && eventStart <= todayEnd;
      });

      setTodaysEvents(filteredEvents);
    } catch (error) {
      console.error('Error fetching today\'s events:', error);
      setTodaysEvents([]);
    }
  }, [user?.id]);

  useEffect(() => {
    if (urlDateSet && user?.id) {
      fetchEventsForCurrentView();
      fetchTodaysEvents();
      setUrlDateSet(false);
    }
  }, [urlDateSet, user?.id, fetchEventsForCurrentView, fetchTodaysEvents]);

  const checkGoogleCalendarStatus = async () => {
    try {
      const response = await calendarService.googleCalendar.getConnectionStatus();
      setGoogleCalendarStatus(response.data);
    } catch (error) {
      console.error('Error checking Google Calendar status:', error);
    }
  };

  const checkOutlookCalendarStatus = async () => {
    try {
      const response = await calendarService.outlookCalendar.getConnectionStatus();
      setOutlookCalendarStatus(response.data);
    } catch (error) {
      console.error('Error checking Outlook Calendar status:', error);
    }
  };

  const anyCalendarConnected = googleCalendarStatus.connected || outlookCalendarStatus.connected;

  const handleSyncComplete = () => {
    fetchEventsForCurrentView();
  };

  // Poll Google Calendar for new events every 60s while on this page
  useGoogleCalendarSync({
    onSyncComplete: () => fetchEventsForCurrentView()
  });

  // Poll Outlook Calendar for new events every 60s while on this page
  useOutlookCalendarSync({
    onSyncComplete: () => fetchEventsForCurrentView()
  });

  const handleSelectSlot = useCallback((slotInfo) => {
    setSelectedSlot({
      start: new Date(slotInfo.start),
      end: new Date(slotInfo.end)
    });
    setSelectedEvent(null);
    setShowEventForm(true);
  }, []);

  const handleSelectEvent = useCallback((event) => {
    if (event.resource?.isBlocked) {
      setSelectedEvent(event.resource.rawPeriod);
      setShowEventDetails(true);
    } else {
      setSelectedEvent(event.resource.rawEvent);
      setShowEventDetails(true);
    }
  }, []);

  const handleEventUpdated = useCallback(() => {
    fetchEventsForCurrentView();
    fetchTodaysEvents();
    setShowEventForm(false);
    setShowEventDetails(false);
    setSelectedEvent(null);
  }, [fetchEventsForCurrentView, fetchTodaysEvents]);

  const handleEventCreated = useCallback(() => {
    fetchEventsForCurrentView();
    fetchTodaysEvents();
    setShowEventForm(false);
    setSelectedSlot(null);
  }, [fetchEventsForCurrentView, fetchTodaysEvents]);

  const handleNavigate = useCallback((date) => {
    setCurrentDate(date);
  }, []);

  const handleViewChange = useCallback((view) => {
    setCurrentView(view);
  }, []);

  const handleDeleteEvent = useCallback((eventId) => {
    setEventToDelete(eventId);
    setShowDeleteConfirm(true);
  }, []);

  const confirmDeleteEvent = useCallback(async () => {
    if (!eventToDelete) return;

    try {
      await calendarService.deleteEvent(eventToDelete);
      fetchEventsForCurrentView();
      fetchTodaysEvents();
      setShowEventDetails(false);
      setSelectedEvent(null);
      showSuccess('Event deleted successfully');
    } catch (error) {
      console.error('Error deleting event:', error);
      showError('Failed to delete event');
    } finally {
      setShowDeleteConfirm(false);
      setEventToDelete(null);
    }
  }, [eventToDelete, fetchEventsForCurrentView, fetchTodaysEvents, showSuccess, showError]);

  // Dynamic event styling based on color scheme
  const eventStyleGetter = useCallback((event) => {
    if (event.resource?.isBlocked) {
      return {
        style: {
          backgroundColor: '#f3f4f6',
          borderRadius: '6px',
          opacity: 0.8,
          color: '#6b7280',
          border: '2px dashed #d1d5db',
          fontStyle: 'italic'
        }
      };
    }

    // Task-specific styling
    if (event.resource?.isTask) {
      return {
        style: {
          backgroundColor: '#f0fdf4',
          borderRadius: '6px',
          color: '#15803d',
          border: '2px solid #86efac',
          fontSize: '0.85em'
        }
      };
    }

    let color = { bg: '#3b82f6', border: '#2563eb' };

    switch (colorScheme) {
      case 'case':
        color = getCaseColor(event.resource?.caseId);
        break;
      case 'type':
        color = EVENT_TYPE_COLORS[event.resource?.type] || EVENT_TYPE_COLORS.other;
        break;
      case 'attorney':
        color = getAttorneyColor(event.resource?.assignedToId);
        break;
      case 'minimal':
        color = { bg: '#3b82f6', border: '#2563eb' };
        break;
      default:
        color = { bg: '#3b82f6', border: '#2563eb' };
    }

    return {
      style: {
        backgroundColor: color.bg,
        borderRadius: '6px',
        border: `2px solid ${color.border}`,
        color: 'white',
        fontWeight: '500'
      }
    };
  }, [colorScheme]);

  // Event Details Modal
  const EventDetailsModal = () => {
    const { handleBackdropClick } = useModal(!!selectedEvent, () => setShowEventDetails(false));

    if (!selectedEvent) return null;
    const isBlockedPeriod = selectedEvent.reason !== undefined;

    return (
      <div
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        onClick={handleBackdropClick}
      >
        <div
          className="bg-white rounded-xl shadow-2xl max-w-lg w-full"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-5 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">
              {isBlockedPeriod ? 'Blocked Time' : 'Event Details'}
            </h2>
            <button
              onClick={() => setShowEventDetails(false)}
              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-5 space-y-4">
            {isBlockedPeriod ? (
              <>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {selectedEvent.reason || 'Blocked Time'}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {selectedEvent.is_recurring ? 'Recurring' : 'One-time'} blocked time
                  </p>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Clock className="h-4 w-4" />
                  <span className="text-sm">
                    {moment(selectedEvent.start_time).format('MMM D, YYYY h:mm A')} - {moment(selectedEvent.end_time).format('h:mm A')}
                  </span>
                </div>
                {selectedEvent.description && (
                  <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">{selectedEvent.description}</p>
                )}
              </>
            ) : (
              <>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{selectedEvent.title}</h3>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                      {selectedEvent.event_type?.replace('_', ' ')}
                    </span>
                    {selectedEvent.case_title && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        {selectedEvent.case_title}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 text-gray-600">
                  <Clock className="h-4 w-4" />
                  <span className="text-sm">
                    {selectedEvent.all_day
                      ? (moment.utc(selectedEvent.start_time).isSame(moment.utc(selectedEvent.end_time), 'day')
                        ? moment.utc(selectedEvent.start_time).format('MMM D, YYYY') + ' (All day)'
                        : moment.utc(selectedEvent.start_time).format('MMM D, YYYY') + ' - ' + moment.utc(selectedEvent.end_time).format('MMM D, YYYY') + ' (All day)')
                      : moment(selectedEvent.start_time).format('MMM D, YYYY h:mm A') + ' - ' + moment(selectedEvent.end_time).format('h:mm A')
                    }
                  </span>
                </div>

                {selectedEvent.assigned_to_name && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <User className="h-4 w-4" />
                    <span className="text-sm">{selectedEvent.assigned_to_name}</span>
                  </div>
                )}

                {selectedEvent.location && (
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Location:</span> {selectedEvent.location}
                  </div>
                )}

                {selectedEvent.description && (
                  <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">{selectedEvent.description}</p>
                )}
              </>
            )}
          </div>

          <div className="flex items-center justify-between p-5 border-t border-gray-100 bg-gray-50 rounded-b-xl">
            <div>
              {!isBlockedPeriod && anyCalendarConnected && (
                <SyncEventButton
                  eventId={selectedEvent.id}
                  isSynced={selectedEvent.external_calendar_syncs?.google || selectedEvent.external_calendar_syncs?.outlook}
                  onSyncComplete={handleSyncComplete}
                  googleConnected={googleCalendarStatus.connected}
                  outlookConnected={outlookCalendarStatus.connected}
                />
              )}
            </div>

            <div className="flex gap-2">
              {isBlockedPeriod ? (
                <Link
                  to="/calendar/settings"
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Manage in Settings
                </Link>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setShowEventDetails(false);
                      setShowEventForm(true);
                    }}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <Edit className="h-4 w-4" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteEvent(selectedEvent.id)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Color Scheme Selector
  const ColorSchemeSelector = () => (
    <div className="relative">
      <button
        onClick={() => setShowColorSchemeMenu(!showColorSchemeMenu)}
        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
      >
        <Palette className="h-4 w-4" />
        <span className="hidden sm:inline">{COLOR_SCHEMES[colorScheme].label}</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${showColorSchemeMenu ? 'rotate-180' : ''}`} />
      </button>

      {showColorSchemeMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowColorSchemeMenu(false)} />
          <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 z-50 overflow-hidden">
            <div className="p-2">
              {Object.values(COLOR_SCHEMES).map((scheme) => {
                const Icon = scheme.icon;
                return (
                  <button
                    key={scheme.id}
                    onClick={() => handleColorSchemeChange(scheme.id)}
                    className={`w-full flex items-start gap-3 p-3 rounded-lg text-left transition-colors ${
                      colorScheme === scheme.id
                        ? 'bg-primary-50 text-primary-700'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <Icon className="h-5 w-5 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium">{scheme.label}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{scheme.description}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );

  // Staff Filter
  const toggleStaffId = (id) => {
    setSelectedStaffIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      // Don't allow empty selection - keep at least one
      if (next.size === 0) return prev;
      return next;
    });
  };

  const selectAllStaff = () => {
    setSelectedStaffIds(new Set(firmUsers.map(u => u.id)));
  };

  const selectOnlyMe = () => {
    setSelectedStaffIds(new Set([user?.id]));
  };

  const staffFilterLabel = useMemo(() => {
    if (selectedStaffIds.size === 1 && selectedStaffIds.has(user?.id)) return 'My Calendar';
    if (firmUsers.length > 0 && selectedStaffIds.size === firmUsers.length) return 'All Staff';
    return `${selectedStaffIds.size} Staff`;
  }, [selectedStaffIds, firmUsers, user?.id]);

  const StaffFilterSelector = () => (
    <div className="relative">
      <button
        onClick={() => setShowStaffFilter(!showStaffFilter)}
        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
      >
        <Users className="h-4 w-4" />
        <span className="hidden sm:inline">{staffFilterLabel}</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${showStaffFilter ? 'rotate-180' : ''}`} />
      </button>

      {showStaffFilter && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowStaffFilter(false)} />
          <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 z-50 overflow-hidden">
            <div className="p-2 border-b border-gray-100 flex gap-1">
              <button
                onClick={selectOnlyMe}
                className="flex-1 px-2 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Only Me
              </button>
              <button
                onClick={selectAllStaff}
                className="flex-1 px-2 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                All Staff
              </button>
            </div>
            <div className="p-1 max-h-[280px] overflow-y-auto">
              {firmUsers.map((firmUser) => {
                const isSelected = selectedStaffIds.has(firmUser.id);
                const displayName = `${firmUser.first_name || ''} ${firmUser.last_name || ''}`.trim() || firmUser.email;
                const isMe = firmUser.id === user?.id;

                return (
                  <button
                    key={firmUser.id}
                    onClick={() => toggleStaffId(firmUser.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition-colors ${
                      isSelected ? 'bg-primary-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${
                      isSelected ? 'bg-primary-600 border-primary-600' : 'border-gray-300'
                    }`}>
                      {isSelected && <Check className="h-3 w-3 text-white" />}
                    </div>
                    <div
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: getAttorneyColor(firmUser.id).bg }}
                    />
                    <span className="text-sm text-gray-700 truncate">
                      {displayName}{isMe ? ' (Me)' : ''}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );

  // Today's Schedule Panel
  const TodaysSchedulePanel = () => (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <button
        onClick={() => setIsTodayScheduleExpanded(!isTodayScheduleExpanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Clock className="h-4 w-4 text-primary-600" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-gray-900">Today's Schedule</h3>
            <p className="text-xs text-gray-500">{todaysEvents.length} event{todaysEvents.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
        {isTodayScheduleExpanded ? (
          <ChevronUp className="h-5 w-5 text-gray-400" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-400" />
        )}
      </button>

      {isTodayScheduleExpanded && (
        <div className="border-t border-gray-100">
          {todaysEvents.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {todaysEvents.map((event) => {
                const eventColor = colorScheme === 'case'
                  ? getCaseColor(event.case_id)
                  : colorScheme === 'type'
                  ? EVENT_TYPE_COLORS[event.event_type] || EVENT_TYPE_COLORS.other
                  : { bg: '#3b82f6' };

                return (
                  <div key={event.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start gap-3">
                      <div
                        className="w-1 h-12 rounded-full flex-shrink-0"
                        style={{ backgroundColor: eventColor.bg }}
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 truncate">{event.title}</h4>
                        <p className="text-sm text-gray-500 mt-0.5">
                          {moment(event.start_time).format('h:mm A')} - {moment(event.end_time).format('h:mm A')}
                        </p>
                        {event.case_title && (
                          <p className="text-xs text-gray-400 mt-1 truncate">{event.case_title}</p>
                        )}
                      </div>
                      {anyCalendarConnected && (
                        <SyncEventButton
                          eventId={event.id}
                          isSynced={event.external_calendar_syncs?.google || event.external_calendar_syncs?.outlook}
                          onSyncComplete={handleSyncComplete}
                          googleConnected={googleCalendarStatus.connected}
                          outlookConnected={outlookCalendarStatus.connected}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-8 text-center">
              <CalendarIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No events scheduled for today</p>
            </div>
          )}
        </div>
      )}
    </div>
  );

  // Legend Panel
  const LegendPanel = () => {
    if (colorScheme === 'minimal' || legendItems.length === 0) return null;

    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Legend</h3>
        <div className="space-y-2">
          {legendItems.map((item) => {
            const content = (
              <>
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: item.color.bg }}
                />
                <span className="text-sm text-gray-600 truncate group-hover:text-gray-900">{item.title}</span>
              </>
            );

            // Make cases clickable
            if (colorScheme === 'case') {
              return (
                <Link
                  key={item.id}
                  to={`/cases/${item.id}`}
                  className="flex items-center gap-2 group hover:bg-gray-50 -mx-2 px-2 py-1 rounded transition-colors"
                >
                  {content}
                </Link>
              );
            }

            return (
              <div key={item.id} className="flex items-center gap-2">
                {content}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading && events.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading calendar...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CalendarDays className="h-7 w-7 text-primary-600" />
            Calendar
          </h1>
          <p className="text-gray-500 mt-1">Manage your schedule and appointments</p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowTasks(!showTasks)}
            className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium border rounded-lg transition-colors ${
              showTasks
                ? 'text-green-700 bg-green-50 border-green-200 hover:bg-green-100'
                : 'text-gray-700 bg-white border-gray-200 hover:bg-gray-50'
            }`}
            title={showTasks ? 'Hide tasks on calendar' : 'Show tasks on calendar'}
          >
            <Check className={`h-4 w-4 ${showTasks ? '' : 'invisible'}`} />
            <span className="hidden sm:inline">Tasks</span>
          </button>
          <StaffFilterSelector />
          <ColorSchemeSelector />

          <button
            onClick={() => {
              setSelectedSlot(null);
              setSelectedEvent(null);
              setShowEventForm(true);
            }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">New Event</span>
          </button>

          <Link
            to="/calendar/settings"
            className="inline-flex items-center gap-2 px-3 py-2 text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Settings className="h-4 w-4" />
          </Link>
        </div>
      </div>

      {/* External Calendar Status */}
      {anyCalendarConnected && (
        <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2 w-fit">
          <div className="w-2 h-2 bg-green-500 rounded-full" />
          {[
            googleCalendarStatus.connected && 'Google',
            outlookCalendarStatus.connected && 'Outlook'
          ].filter(Boolean).join(' & ')} Calendar connected
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Calendar */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {currentView === 'agenda' ? (
              <CustomAgendaView
                events={[...events, ...blockedPeriods].map(event => ({
                  ...event,
                  event_type: event.resource?.type,
                  location: event.resource?.location,
                  description: event.resource?.description,
                  external_calendar_syncs: event.resource?.rawEvent?.external_calendar_syncs
                }))}
                date={currentDate}
                onSelectEvent={handleSelectEvent}
              />
            ) : (
              <BigCalendar
                localizer={localizer}
                events={[...events, ...blockedPeriods]}
                startAccessor="start"
                endAccessor="end"
                style={{ height: 650 }}
                eventPropGetter={eventStyleGetter}
                views={['month', 'week', 'day', 'agenda']}
                defaultView="week"
                date={currentDate}
                view={currentView}
                onNavigate={handleNavigate}
                onView={handleViewChange}
                popup
                selectable
                onSelectSlot={handleSelectSlot}
                onSelectEvent={handleSelectEvent}
                scrollToTime={new Date(1970, 1, 1, 7)}
                min={new Date(1970, 1, 1, 0, 0, 0)}
                showMultiDayTimes={true}
                tooltipAccessor={() => null}
                components={{
                  eventWrapper: ({ event, children }) => (
                    <EventTooltip event={event} asWrapper>
                      {children}
                    </EventTooltip>
                  )
                }}
              />
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <TodaysSchedulePanel />
          <LegendPanel />
        </div>
      </div>

      {/* Modals */}
      {showEventForm && (
        <CalendarEventForm
          event={selectedEvent}
          initialDateTime={selectedSlot}
          onClose={() => {
            setShowEventForm(false);
            setSelectedEvent(null);
            setSelectedSlot(null);
          }}
          onEventCreated={handleEventCreated}
          onEventUpdated={handleEventUpdated}
        />
      )}

      {showEventDetails && <EventDetailsModal />}

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setEventToDelete(null);
        }}
        onConfirm={confirmDeleteEvent}
        title="Delete Event"
        message="Are you sure you want to delete this event? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      <Notification
        isOpen={notification.isOpen}
        onClose={hideNotification}
        title={notification.title}
        message={notification.message}
        variant={notification.variant}
        duration={notification.duration}
      />
    </div>
  );
};

export default Calendar;
